import { TableKit } from "@tiptap/extension-table"
import TextAlign from "@tiptap/extension-text-align"
import { EditorContent, useEditor } from "@tiptap/react"
import StarterKit from "@tiptap/starter-kit"
import {
  AlignCenter,
  AlignJustify,
  AlignLeft,
  AlignRight,
  Bold,
  Columns,
  Heading2,
  Heading3,
  Italic,
  List,
  ListOrdered,
  Rows,
  ScissorsLineDashed,
  Table as TableIcon,
  Trash2,
} from "lucide-react"
import { type ReactNode, useEffect, useReducer } from "react"

import { cn } from "@/lib/cn"

import type { NodoDoc } from "./Documento"
import { SaltoDePagina } from "./saltoDePagina"

/**
 * El editor de un documento controlado.
 *
 * ⚠ **Devuelve JSON, no HTML.** Es lo que permite pintarlo con `Documento.tsx` sin
 * `dangerouslySetInnerHTML` (regla 12). Ver la cabecera de ese archivo.
 *
 * ⚠ **`onMouseDown` se cancela en cada botón de la barra.** Sin eso, pulsar un botón
 * desenfoca el editor y colapsa la selección, y los comandos de tabla —que actúan
 * sobre dónde está el cursor— no encuentran celda. Se manifiesta como botones que no
 * hacen nada sólo dentro de una tabla, que es donde más se usan.
 *
 * ⚠ **La barra se repinta al mover el cursor.** Los estados activo y deshabilitado
 * dependen de dónde esté el cursor —«añadir fila» sólo vale dentro de una tabla—, y
 * sin esto se quedan congelados en lo que valían al montar.
 */

function Btn({
  al,
  activo,
  inactivo,
  peligro,
  titulo,
  children,
}: {
  al: () => void
  activo?: boolean
  inactivo?: boolean
  peligro?: boolean
  titulo: string
  children: ReactNode
}) {
  return (
    <button
      type="button"
      title={titulo}
      aria-label={titulo}
      aria-pressed={activo}
      onMouseDown={(e) => e.preventDefault()}
      onClick={al}
      disabled={inactivo}
      className={cn(
        "flex h-8 w-8 items-center justify-center rounded-[var(--radius-control)] transition-colors",
        "hover:bg-[var(--surface-elevated)] disabled:cursor-not-allowed disabled:opacity-30",
      )}
      style={{
        color: peligro ? "var(--destructive)" : activo ? "var(--primary-texto)" : "var(--fg-muted)",
        backgroundColor: activo ? "var(--surface-elevated)" : undefined,
      }}
    >
      {children}
    </button>
  )
}

export function EditorDocumento({
  valor,
  alCambiar,
}: {
  valor: NodoDoc | null
  alCambiar: (cuerpo: NodoDoc) => void
}) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] } }),
      TableKit.configure({ table: { resizable: false } }),
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      SaltoDePagina,
    ],
    content: valor ?? { type: "doc", content: [{ type: "paragraph" }] },
    onUpdate: ({ editor }) => alCambiar(editor.getJSON() as NodoDoc),
    editorProps: { attributes: { class: "doc-prosa min-h-[420px] focus:outline-none" } },
  })

  const [, repintar] = useReducer((x: number) => x + 1, 0)
  useEffect(() => {
    if (!editor) return
    const al = () => repintar()
    editor.on("selectionUpdate", al)
    editor.on("transaction", al)
    return () => {
      editor.off("selectionUpdate", al)
      editor.off("transaction", al)
    }
  }, [editor])

  if (!editor) return null
  const ico = 16
  const enTabla = editor.isActive("table")

  return (
    <div
      className="flex flex-col overflow-hidden rounded-[var(--radius-tarjeta)] border"
      style={{ borderColor: "var(--border)", backgroundColor: "var(--surface)" }}
    >
      <div
        className="flex shrink-0 flex-wrap items-center gap-1 border-b px-2 py-1.5"
        style={{ borderColor: "var(--border)" }}
      >
        <Btn
          al={() => editor.chain().focus().toggleBold().run()}
          activo={editor.isActive("bold")}
          titulo="Negrita"
        >
          <Bold size={ico} aria-hidden="true" />
        </Btn>
        <Btn
          al={() => editor.chain().focus().toggleItalic().run()}
          activo={editor.isActive("italic")}
          titulo="Cursiva"
        >
          <Italic size={ico} aria-hidden="true" />
        </Btn>
        <span className="mx-1 h-5 w-px" style={{ backgroundColor: "var(--border)" }} />
        <Btn
          al={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          activo={editor.isActive("heading", { level: 2 })}
          titulo="Sección"
        >
          <Heading2 size={ico} aria-hidden="true" />
        </Btn>
        <Btn
          al={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          activo={editor.isActive("heading", { level: 3 })}
          titulo="Subsección"
        >
          <Heading3 size={ico} aria-hidden="true" />
        </Btn>
        <Btn
          al={() => editor.chain().focus().toggleBulletList().run()}
          activo={editor.isActive("bulletList")}
          titulo="Viñetas"
        >
          <List size={ico} aria-hidden="true" />
        </Btn>
        <Btn
          al={() => editor.chain().focus().toggleOrderedList().run()}
          activo={editor.isActive("orderedList")}
          titulo="Lista numerada"
        >
          <ListOrdered size={ico} aria-hidden="true" />
        </Btn>
        <span className="mx-1 h-5 w-px" style={{ backgroundColor: "var(--border)" }} />
        <Btn al={() => editor.chain().focus().setTextAlign("left").run()} titulo="Izquierda">
          <AlignLeft size={ico} aria-hidden="true" />
        </Btn>
        <Btn al={() => editor.chain().focus().setTextAlign("center").run()} titulo="Centrar">
          <AlignCenter size={ico} aria-hidden="true" />
        </Btn>
        <Btn al={() => editor.chain().focus().setTextAlign("right").run()} titulo="Derecha">
          <AlignRight size={ico} aria-hidden="true" />
        </Btn>
        <Btn al={() => editor.chain().focus().setTextAlign("justify").run()} titulo="Justificar">
          <AlignJustify size={ico} aria-hidden="true" />
        </Btn>
        <span className="mx-1 h-5 w-px" style={{ backgroundColor: "var(--border)" }} />
        <Btn
          al={() =>
            editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()
          }
          titulo="Insertar tabla"
        >
          <TableIcon size={ico} aria-hidden="true" />
        </Btn>
        <Btn
          al={() => editor.chain().focus().addRowAfter().run()}
          inactivo={!enTabla}
          titulo="Añadir fila"
        >
          <Rows size={ico} aria-hidden="true" />
        </Btn>
        <Btn
          al={() => editor.chain().focus().deleteRow().run()}
          inactivo={!enTabla}
          peligro
          titulo="Eliminar la fila"
        >
          <Rows size={ico} aria-hidden="true" />
        </Btn>
        <Btn
          al={() => editor.chain().focus().addColumnAfter().run()}
          inactivo={!enTabla}
          titulo="Añadir columna"
        >
          <Columns size={ico} aria-hidden="true" />
        </Btn>
        <Btn
          al={() => editor.chain().focus().deleteColumn().run()}
          inactivo={!enTabla}
          peligro
          titulo="Eliminar la columna"
        >
          <Columns size={ico} aria-hidden="true" />
        </Btn>
        <Btn
          al={() => editor.chain().focus().deleteTable().run()}
          inactivo={!enTabla}
          peligro
          titulo="Eliminar la tabla"
        >
          <Trash2 size={ico} aria-hidden="true" />
        </Btn>
        <span className="mx-1 h-5 w-px" style={{ backgroundColor: "var(--border)" }} />
        <Btn
          al={() => editor.chain().focus().insertContent({ type: "saltoDePagina" }).run()}
          titulo="Salto de página (hoja nueva al imprimir)"
        >
          <ScissorsLineDashed size={ico} aria-hidden="true" />
        </Btn>
      </div>
      <div className="max-h-[60vh] flex-1 overflow-y-auto px-5 py-4">
        <EditorContent editor={editor} />
      </div>
    </div>
  )
}
